/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import Icon from 'components/Icon/Icon';
import { Constants } from 'constants/Constants';
import { useState, useCallback, useEffect, ReactNode, useMemo } from 'react';

import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import useAuth from 'common/hooks/useAuth';
import { Dots } from 'react-activity';
import Button from '../Button/Button';
import ThreadGallery from '../ThreadGallery/ThreadGallery';
import DefaultFilesList from './DefaultFilesList';

type Props = {
  value?: any;
  accept?: string;
  children?: ReactNode;
  onChange?: (values: any) => void;
  nbCols?: number;
  label?: string;
  maxSize?: number;
  max?: number;
  name?: string;
  onUpload?: (value: any) => void;
  type?: string;
  limit?: number;
  autoSubmit?: boolean;
};

function FileUploader({
  value,
  accept,
  onChange = () => {},
  maxSize = Infinity,
  max = Infinity,
  onUpload,
  // "data-cy": dataCy,
  type = 'documents',
  limit = Infinity,
  autoSubmit = false,
  ...rest
}: Props) {
  const [files, setFiles] = useState([]);
  const uploadUrl = `${Constants.API_URL}/upload`;
  const [uploadingFiles, setUploadingFiles] = useState({});
  const { token }: any = useAuth();

  const onRemoveFile = useCallback(
    (removeFile: any) => {
      const newFiles = files.filter((f: any) => {
        if (f.hash) return f.hash !== removeFile.hash;
        return f.id !== removeFile.id;
      });
      onChange(newFiles);
    },
    [files, onChange]
  );

  const sendFile = useCallback(
    async (file: any) =>
      new Promise((resolve) => {
        const req = new XMLHttpRequest();

        req.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            setUploadingFiles((prevFiles: any) => ({
              ...prevFiles,
              [file.name]: {
                ...file,
                state: 'pending',
                percentage: (event.loaded / event.total) * 100,
              },
            }));
          }
        });

        req.upload.addEventListener('load', () => {
          setUploadingFiles((prevFiles: any) => ({
            ...prevFiles,
            [file.name]: {
              ...file,
              state: 'done',
              percentage: 100,
            },
          }));
        });

        req.upload.addEventListener('error', () => {
          setUploadingFiles((prevFiles: any) => ({
            ...prevFiles,
            [file.name]: {
              ...prevFiles[file.name],
              state: 'error',
              percentage: 0,
            },
          }));
          resolve(null);
        });

        // eslint-disable-next-line func-names
        req.addEventListener('load', function () {
          try {
            // eslint-disable-next-line react/no-this-in-sfc
            const response = JSON.parse(this.response);

            const res = Array.isArray(response) ? response[0] : response;

            if (onUpload) {
              onUpload(res);
            }

            resolve({
              ...res,
              state: 'done',
              percentage: 100,
              filename: res.filename ?? res.name,
              name: res.filename ?? res.name,
            });
          } catch (e) {
            console.error(e);
            resolve({
              ...file,
              state: 'error',
              percentage: 0,
              filename: 'titi',
            });
          }
        });

        const formData = new FormData();

        formData.append('files', file.file);
        req.onload = () => {
          // console.log(req.responseText);
        };

        req.open('POST', uploadUrl, true);
        req.setRequestHeader('Authorization', `Bearer ${token}`);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        req.send(formData);
      }),
    [uploadUrl, token, onUpload]
  );

  const handleFileSelected = useCallback(
    async (inputFiles: any) => {
      const inputFilesQuota = limit ? limit - value.length : inputFiles.length;
      if (!inputFiles.length || files.length + inputFiles.length > max) {
        return;
      }
      const fileslist = [] as any[];
      // eslint-disable-next-line no-plusplus
      inputFiles.slice(0, inputFilesQuota).forEach((inputFile: File) => {
        const maxIndex = Math.max(...fileslist.map((currentFile) => currentFile.index));
        const file: any = {
          index: fileslist.length ? maxIndex + 1 : 0,
          inputFile,
          name: inputFile.name,
          url: URL.createObjectURL(inputFile),
        };

        if (file.inputFile.size <= maxSize) {
          fileslist.push(file);
        }
      });

      const tmpFiles = fileslist;

      const filesToUpload = {} as any;

      for (const file of tmpFiles) {
        filesToUpload[file.name] = {
          name: file.name,
          url: file.url,
          file: file.inputFile,
          type: file.inputFile.type,
          state: 'pending',
          percentage: 0,
        };
      }
      setUploadingFiles(filesToUpload);

      const promiseFiles = [] as any;

      Object.values(filesToUpload).forEach((f) => {
        promiseFiles.push(sendFile(f));
      });
      const uploadedFiles = await Promise.all(promiseFiles);

      const newFiles = [...files] as any;

      uploadedFiles.forEach((file) => {
        if (file) newFiles.push(file);
      });
      setFiles(newFiles);
      onChange(newFiles);

      setUploadingFiles({});
    },
    [files, max, maxSize, onChange, sendFile, limit, value]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileSelected,
    multiple: true,
    accept: accept ? { [accept]: [] } : undefined,
    disabled: value.length >= (limit ?? 0),
  });

  const list = useMemo(() => {
    // Si autoSubmit est activé, ne pas afficher la liste des fichiers
    if (autoSubmit) {
      return null;
    }

    const ufiles = Object.values(uploadingFiles) ?? [];
    const fileslist = [...files, ...ufiles];

    if (fileslist.length < 1) {
      return null;
    }

    if (type === 'documents') {
      return (
        <DefaultFilesList
          items={fileslist}
          onChange={onChange}
          onRemoveFile={onRemoveFile}
          editMode
        />
      );
    }

    return (
      <ThreadGallery
        documents={fileslist}
        onRemoveFile={onRemoveFile}
        small={type === 'file-comment'}
        editMode
      />
    );
  }, [type, uploadingFiles, onChange, onRemoveFile, files, autoSubmit]);

  const { t } = useTranslation();

  const addIcon = useMemo(() => {
    const ufiles = Object.values(uploadingFiles) ?? [];
    const fileslist = [...files, ...ufiles];

    const isLoading = fileslist?.find((item: any) => item.state === 'pending') as any;

    const className =
      'flex cursor-pointer gap-1 mb-1 text-sm items-center justify-center w-full rounded-3xl py-2 px-3 ' +
      'border border-dashed border-secondary/40 text-fourth shadow-lg ' +
      'hover:opacity-70 hover:border-solid hover:bg-white hover:shadow-md ' +
      'active:shadow-sm active:bg-white active:border-2';

    if (type === 'documents') {
      return (
        <div className={className}>
          <Icon name="PlusCircleIcon" className="w-4 h-4" />
          {t('newItem.doc')}
        </div>
      );
    }

    if (type === 'images') {
      return (
        <div className={className + 'flex-col h-full'}>
          {isLoading ? (
            <>
              <span className="text-sm text-slate-700">
                {(isLoading.percentage || 0).toFixed(0)} %
              </span>

              <Dots />
            </>
          ) : (
            <>
              <Icon name="PlusCircleIcon" className="w-8 h-8" />
              {t('forms.uploadPicture')}
            </>
          )}
        </div>
      );
    }
    return <Button type="neutral-line" icon="AttachedFile" compact />;
  }, [type, t, files, uploadingFiles]);

  useEffect(() => {
    if (value) {
      setFiles(value);
    } else {
      setFiles([]);
    }
  }, [value, setFiles]);

  return (
    <div {...rest} className={type === 'images' ? 'flex gap-1' : ''}>
      <div
        {...getRootProps()}
        className={`${files.length >= max ? 'hidden' : ''} ${type === 'images' ? 'w-36 h-36' : ''}`}
      >
        <input {...getInputProps()} />
        {addIcon}
      </div>

      {/* N'afficher la liste que si autoSubmit est désactivé */}
      {!autoSubmit && list}
    </div>
  );
}

export default FileUploader;
